import { useCallback, useEffect, useState } from "react"
import { useLocalStorage, useSessionStorage } from "usehooks-ts"
import { type z } from "zod"
import useOrgId from "~/hooks/useOrgId"
import { useAuth0 } from "@auth0/auth0-react"

const scopedStorageKey = (orgId: string | undefined, uniqueKey: string, crossCompany: boolean) =>
  !crossCompany ? `${orgId}.${uniqueKey}` : `template-${uniqueKey}`

function readStoredValue(storage: Storage, fullKey: string, initialValue: unknown): unknown {
  try {
    const raw = storage.getItem(fullKey)
    if (raw === null) return initialValue
    // usehooks-ts reads a stored "undefined" as undefined; match it so both renders agree.
    return raw === "undefined" ? undefined : JSON.parse(raw)
  } catch {
    return initialValue
  }
}

// usehooks-ts re-reads a changed key only in an effect, which runs after the children's effects,
// so for one render its value still belongs to the previous key. Read the current key directly then.
// Returns `initialValue` for anything the schema rejects.
function useCurrentKeyValue<T>(
  storage: "localStorage" | "sessionStorage",
  fullKey: string,
  storedValue: T,
  initialValue: T,
  zodSchema: z.ZodType<T>,
): T {
  const [syncedKey, setSyncedKey] = useState(fullKey)
  useEffect(() => setSyncedKey(fullKey), [fullKey])
  const value =
    syncedKey === fullKey ? storedValue : readStoredValue(window[storage], fullKey, initialValue)
  // The stored value itself, not `safeParse().data`: that is a new (key-stripped) copy each render.
  return zodSchema.safeParse(value).success ? (value as T) : initialValue
}

export function useSafeLocalStorage<T>(
  uniqueKey: string,
  initialValue: T,
  zodSchema: z.ZodType<T>,
  crossCompany = false,
  saveInLocalStorageOnInitializeIfNotSet = false,
  // The reason you want save in local storage on initialize is because, if there are other components that depend on this value,
  // they will not be able to get the value from local storage if it is not saved on initialize
): [T, (value: T) => void] {
  const orgId = useOrgId()
  const fullKey = scopedStorageKey(orgId, uniqueKey, crossCompany)
  const [storedValue, setStoredValue] = useLocalStorage(fullKey, initialValue)

  useEffect(() => {
    if (!zodSchema.safeParse(storedValue).success) {
      return setStoredValue(initialValue)
    }
    if (saveInLocalStorageOnInitializeIfNotSet && !window.localStorage.getItem(fullKey)) {
      setStoredValue(initialValue)
    }
  }, [
    storedValue,
    zodSchema,
    initialValue,
    fullKey,
    saveInLocalStorageOnInitializeIfNotSet,
    setStoredValue,
  ])

  // Stable identity so consumers can safely list this in effect deps without
  // re-running every render.
  const setValue = useCallback((value: T) => setStoredValue(value), [setStoredValue])

  return [
    useCurrentKeyValue("localStorage", fullKey, storedValue, initialValue, zodSchema),
    setValue,
  ]
}

export function useSafeSessionStorage<T>(
  uniqueKey: string,
  initialValue: T,
  zodSchema: z.ZodType<T>,
  crossCompany = false,
  saveInSessionStorageOnInitializeIfNotSet = false,
  // The reason you want save in local storage on initialize is because, if there are other components that depend on this value,
  // they will not be able to get the value from local storage if it is not saved on initialize
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const { user } = useAuth0()

  const fullKey = scopedStorageKey(user?.org_id, uniqueKey, crossCompany)
  const [storedValue, setStoredValue, clearStoredValue] = useSessionStorage(fullKey, initialValue)
  useEffect(() => {
    if (!zodSchema.safeParse(storedValue).success) {
      return setStoredValue(initialValue)
    }
    if (saveInSessionStorageOnInitializeIfNotSet && !window.sessionStorage.getItem(fullKey)) {
      setStoredValue(initialValue)
    }
  }, [
    storedValue,
    zodSchema,
    initialValue,
    fullKey,
    saveInSessionStorageOnInitializeIfNotSet,
    setStoredValue,
  ])

  // Stable identity so consumers can safely list this in effect deps without
  // re-running every render.
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => setStoredValue(value),
    [setStoredValue],
  )

  return [
    useCurrentKeyValue("sessionStorage", fullKey, storedValue, initialValue, zodSchema),
    setValue,
    clearStoredValue,
  ]
}
